import { useEffect, useState } from 'react';
import { addDays, mondayOnOrBefore } from '../../../lib/dates/iso.ts';
import { formatPeriod } from '../../../lib/format/dates.ts';
import { ApiFailure, api, type RangeDistribution } from '../../api.ts';
import { Notice } from '../../components/Notice.tsx';
import { copy } from '../../copy/en-CA.ts';

const c = copy.admin.distribution;

function one(n: number): string {
  return (Math.round(n * 10) / 10).toFixed(1);
}

/**
 * Call over any date range across every saved schedule, in absolute counts
 * or weighted by the weeks each physician was available. Defaults to the
 * past twelve months.
 */
export function AdminDistributionPage() {
  const [today, setToday] = useState<string | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [view, setView] = useState<'absolute' | 'weighted'>('absolute');
  const [data, setData] = useState<RangeDistribution | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api
      .schedules()
      .then((r) => {
        setToday(r.today);
        setFrom(mondayOnOrBefore(addDays(r.today, -364)));
        setTo(r.today);
      })
      .catch((err: unknown) => {
        setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
      });
  }, []);

  useEffect(() => {
    if (!from || !to || to < from) return;
    let cancelled = false;
    api.admin
      .rangeDistribution(from, to)
      .then((r) => {
        if (!cancelled) {
          setData(r);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof ApiFailure ? err.message : copy.errors.unexpected);
      });
    return () => {
      cancelled = true;
    };
  }, [from, to]);

  const rows = data ? [...data.rows].sort((a, b) => b.weeks - a.weeks || a.name.localeCompare(b.name)) : [];

  return (
    <>
      <div className="page-title">
        <h1>{c.title}</h1>
        <p>{c.intro}</p>
      </div>
      <section className="card stack">
        <div className="row" role="group" aria-label={c.range}>
          <div className="field">
            <label htmlFor="dist-from">{c.from}</label>
            <input id="dist-from" className="input" type="date" value={from} onChange={(e) => { setFrom(e.target.value); }} />
          </div>
          <div className="field">
            <label htmlFor="dist-to">{c.to}</label>
            <input id="dist-to" className="input" type="date" value={to} onChange={(e) => { setTo(e.target.value); }} />
          </div>
          {today ? (
            <button type="button" className="btn" onClick={() => { setFrom(mondayOnOrBefore(addDays(today, -364))); setTo(today); }}>
              {c.lastYear}
            </button>
          ) : null}
          <div className="grow" />
          <div className="row" role="group" aria-label={c.view}>
            <button type="button" className={`seg ${view === 'absolute' ? 'on' : ''}`} aria-pressed={view === 'absolute'} onClick={() => { setView('absolute'); }}>
              {c.absolute}
            </button>
            <button type="button" className={`seg ${view === 'weighted' ? 'on' : ''}`} aria-pressed={view === 'weighted'} onClick={() => { setView('weighted'); }}>
              {c.weighted}
            </button>
          </div>
        </div>
        {error ? <Notice kind="error">{error}</Notice> : null}
        {data ? <p className="muted">{c.summary(formatPeriod(data.from, data.to), data.scheduledWeeks)}</p> : null}
        {data?.scheduledWeeks === 0 ? <p className="empty">{c.empty}</p> : null}
        {data && data.scheduledWeeks > 0 ? (
          <div className="table-region" role="region" aria-label={c.title} tabIndex={0}>
            <table className="distribution">
              <thead>
                <tr>
                  <th>{c.columns.physician}</th>
                  <th>{c.columns.weeks}</th>
                  <th>{c.columns.longWeekends}</th>
                  <th>{c.columns.special}</th>
                  <th>{c.columns.available}</th>
                  {view === 'weighted' ? (
                    <>
                      <th>{c.columns.expected}</th>
                      <th>{c.columns.difference}</th>
                      <th>{c.columns.perAvailable}</th>
                    </>
                  ) : (
                    <th>{c.columns.leave}</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const diff = r.weeks - r.expectedWeeks;
                  return (
                    <tr key={r.physicianId}>
                      <td>
                        <strong>{r.name}</strong>
                      </td>
                      <td>
                        <span className="stat">{r.weeks}</span>
                      </td>
                      <td>
                        <span className="stat">{r.longWeekends}</span>
                      </td>
                      <td>
                        <span className="stat">{r.specialWeeks}</span>
                      </td>
                      <td className="muted">{c.availableOf(r.availableWeeks, data.scheduledWeeks)}</td>
                      {view === 'weighted' ? (
                        <>
                          <td className="muted">{one(r.expectedWeeks)}</td>
                          <td className={diff > 0.5 ? 'ahead' : diff < -0.5 ? 'behind' : 'muted'}>{r.availableWeeks === 0 ? '–' : (diff > 0 ? '+' : '') + one(diff)}</td>
                          <td className="muted">{r.perAvailableWeek === null ? '–' : one(r.perAvailableWeek * 10)}</td>
                        </>
                      ) : (
                        <td className="muted">{r.leaveWeeks || '–'}</td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
        <p className="muted small">{view === 'weighted' ? c.weightedNote : c.absoluteNote}</p>
      </section>
    </>
  );
}
